import React from 'react';
import { getJobStatusLabel, getJobStatusColor } from '../../types/job';
import './JobDetailModal.css';

const JobDetailModal = ({ job, isLoading = false, onClose }) => {
  if (isLoading) {
    return (
      <div className="job-detail-modal-overlay" onClick={onClose}>
        <div className="job-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="job-detail-modal__loading">
            <div className="job-detail-modal__spinner"></div>
            <p>Loading job details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!job) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="job-detail-modal-overlay" onClick={onClose}>
      <div className="job-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="job-detail-modal__header">
          <div className="job-detail-modal__header-content">
            <h2 className="job-detail-modal__title">Job Details</h2>
            <span
              className="job-detail-modal__status"
              style={{ backgroundColor: getJobStatusColor(job.status) }}
            >
              {getJobStatusLabel(job.status)}
            </span>
          </div>
          <button onClick={onClose} className="job-detail-modal__close">×</button>
        </div>

        <div className="job-detail-modal__content">
          {/* Job Information */}
          <div className="job-detail-modal__section">
            <h3 className="job-detail-modal__section-title">Job Information</h3>
            <div className="job-detail-modal__grid">
              <div className="job-detail-modal__field">
                <label>Job Role</label>
                <p>{job.job_role?.job_role_name || job.job_role_detail?.job_role_name || job.job_role_name || job.job_role || 'N/A'}</p>
              </div>
              <div className="job-detail-modal__field">
                <label>Date</label>
                <p>{formatDate(job.date || job.start_date || job.job_date)}</p>
              </div>
              <div className="job-detail-modal__field">
                <label>Time</label>
                <p>{formatTime(job.start_time)} - {formatTime(job.end_time)}</p>
              </div>
              <div className="job-detail-modal__field">
                <label>Number of Hours</label>
                <p>{job.number_of_hours || job.total_hours || job.hours || 'N/A'} {job.number_of_hours || job.total_hours || job.hours ? 'hours' : ''}</p>
              </div>
            </div>
          </div>

          {/* Employer Information */}
          <div className="job-detail-modal__section">
            <h3 className="job-detail-modal__section-title">Employer Information</h3>
            <div className="job-detail-modal__grid">
              <div className="job-detail-modal__field">
                <label>Employer Name</label>
                <p>
                  {job.employer?.first_name && job.employer?.last_name
                    ? `${job.employer.first_name} ${job.employer.last_name}`
                    : job.employer_name || 'N/A'}
                </p>
              </div>
              <div className="job-detail-modal__field">
                <label>Email</label>
                <p>{job.employer?.email || job.employer_email || 'N/A'}</p>
              </div>
              <div className="job-detail-modal__field">
                <label>Practice Name</label>
                <p>{job.employer?.practice_name || job.employer_practice_name || 'N/A'}</p>
              </div>
              <div className="job-detail-modal__field">
                <label>Practice Address</label>
                <p>{job.employer?.address || job.employer_address || 'N/A'}</p>
              </div>
              <div className="job-detail-modal__field">
                <label>Phone Number</label>
                <p>{job.employer?.contact_no || job.employer_contact_no || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Match Information (if available) */}
          {job.matches && job.matches.length > 0 && (
            <div className="job-detail-modal__section">
              <h3 className="job-detail-modal__section-title">Match Information</h3>
              <div className="job-detail-modal__matches">
                {job.matches.map((match, index) => (
                  <div key={match.id || index} className="job-detail-modal__match-card">
                    <div className="job-detail-modal__match-header">
                      <h4>Match #{index + 1}</h4>
                      <span
                        className="job-detail-modal__match-status"
                        style={{ backgroundColor: getJobStatusColor(match.status || 'pending') }}
                      >
                        {match.status || 'Pending'}
                      </span>
                    </div>
                    <div className="job-detail-modal__match-details">
                      <div className="job-detail-modal__field">
                        <label>Employee</label>
                        <p>
                          {match.employee_detail?.first_name && match.employee_detail?.last_name
                            ? `${match.employee_detail.first_name} ${match.employee_detail.last_name}`
                            : match.employee_name || 'N/A'}
                        </p>
                      </div>
                      <div className="job-detail-modal__field">
                        <label>Employee Email</label>
                        <p>{match.employee_detail?.email || match.employee_email || 'N/A'}</p>
                      </div>
                      {match.distance_miles != null && typeof match.distance_miles !== 'undefined' && (
                        <div className="job-detail-modal__field">
                          <label>Distance</label>
                          <p>
                            {(() => {
                              const distance = typeof match.distance_miles === 'number' 
                                ? match.distance_miles 
                                : parseFloat(match.distance_miles);
                              return !isNaN(distance) ? distance.toFixed(1) : 'N/A';
                            })()} miles
                          </p>
                        </div>
                      )}
                      <div className="job-detail-modal__field">
                        <label>Matched At</label>
                        <p>{formatDateTime(match.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="job-detail-modal__section">
            <h3 className="job-detail-modal__section-title">Timestamps</h3>
            <div className="job-detail-modal__grid">
              <div className="job-detail-modal__field">
                <label>Created At</label>
                <p>{formatDateTime(job.created_at || job.date_created)}</p>
              </div>
              <div className="job-detail-modal__field">
                <label>Last Updated</label>
                <p>{formatDateTime(job.updated_at || job.date_updated)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="job-detail-modal__footer">
          <button
            className="job-detail-modal__button"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobDetailModal;

